import React from 'react';
import { Switch, Route } from 'react-router-dom';
import loadable from '@loadable/component';

import { ROUTES } from 'config/routes';
import {
  ADMIN_LIST,
  DICTIONARY_LIST,
  LOCALIZATION_LIST,
  NEWS_LIST,
  ROLE_LIST,
  SETTINGS_LIST,
  USER_LIST,
  COMPLAINTS_LIST,
  KEYWORDS_LIST,
  KEYWORDS_VIOLATIONS_LIST,
  FAKE_USER_LIST,
  RADAR_LIST,
  GIFT_CATEGORY_VIEW,
  GIFT_VIEW,
  MARKET_LIST,
  COIN_PACKET_VIEW,
  JJFORTUNE_VIEW,
} from 'config/roles';
import Page from 'components/Layout/Page';
import AuthRoute from 'components/Routes/AuthRoute';
import { businessLinesConfig, interestsConfig, languagesSpokenConfig } from './Dictionaries/configs';

const HomePage = loadable(() => import(/* webpackChunkName: "HomePage" */ 'pages/HomePage'));
const Login = loadable(() => import(/* webpackChunkName: "Login" */ 'pages/Login'));
const Administration = loadable(() => import(/* webpackChunkName: "Administration" */ 'pages/Administration'));
const News = loadable(() => import(/* webpackChunkName: "News" */ 'pages/News'));
const Languages = loadable(() => import(/* webpackChunkName: "Languages" */ 'pages/Languages'));
const Users = loadable(() => import(/* webpackChunkName: "Users" */ 'pages/Users'));
const Authors = loadable(() => import(/* webpackChunkName: "Authors" */ 'pages/Authors'));
const Payouts = loadable(() => import(/* webpackChunkName: "Payouts" */ 'pages/Payouts'));
const Locations = loadable(() => import(/* webpackChunkName: "Locations" */ 'pages/Locations'));
const Dictionaries = loadable(() => import(/* webpackChunkName: "Dictionaries" */ 'pages/Dictionaries'));
const Content = loadable(() => import(/* webpackChunkName: "Content" */ 'pages/Content'));
const Settings = loadable(() => import(/* webpackChunkName: "Settings" */ 'pages/Settings'));
const KeyWordsViolations = loadable(() => import(/* webpackChunkName: "KeyWordsViolations" */ 'pages/KeyWordsViolations'));
const Complaints = loadable(() => import(/* webpackChunkName: "Complaints" */ 'pages/Complaints'));
const Radar = loadable(() => import(/* webpackChunkName: "Radar" */ 'pages/Radar'));
const Gifts = loadable(() => import(/* webpackChunkName: "Gifts" */ 'pages/Gifts'));
const Packages = loadable(() => import(/* webpackChunkName: "Packages" */ 'pages/Packages'));
const PaidServices = loadable(() => import(/* webpackChunkName: "PaidServices" */ 'pages/PaidServices'));
const Chats = loadable(() => import(/* webpackChunkName: "Chats" */ 'pages/Chats'));
const JJCoins = loadable(() => import(/* webpackChunkName: "JJCoins" */ 'pages/JJCoins'));
const JJLikesPackages = loadable(() => import(/* webpackChunkName: "JJLikesPackages" */ 'pages/JJLikesPackages'));
const JJFortune = loadable(() => import(/* webpackChunkName: "JJFortune" */ 'pages/JJFortune'));
const JJLikes = loadable(() => import(/* webpackChunkName: "JJLikes" */ 'pages/JJLikes'));
const Agreements = loadable(() => import(/* webpackChunkName: "Agreements" */ 'pages/Agreements'));
const JJPremium = loadable(() => import(/* webpackChunkName: "JJPremium" */ 'pages/JJPremium'));
const JJFun = loadable(() => import(/* webpackChunkName: "JJFun" */ 'pages/JJFun'));
const JJVideo = loadable(() => import(/* webpackChunkName: "JJVideo" */ 'pages/JJVideo'));
const Statistics = loadable(() => import(/* webpackChunkName: "Statistics" */ 'pages/Statistics'));
const AccrualCoins = loadable(() => import(/* webpackChunkName: "Statistics" */ 'pages/AccrualCoins'))
const JJIncome = loadable(() => import(/* webpackChunkName: "JJIncome" */ 'pages/JJIncome'));

const Router = () => {
  return (
    <Switch>
      <Route exact path={ROUTES.LOGIN} component={Login} />
      <Route path="*">
        <Page>
          <Switch>
            <AuthRoute exact path={ROUTES.HOME_PAGE} component={HomePage} />
            <AuthRoute path={ROUTES.ADMINISTRATION} component={Administration} permissions={[ADMIN_LIST, ROLE_LIST]} />
            <AuthRoute path={ROUTES.NEWS} component={News} permissions={NEWS_LIST} />
            <AuthRoute path={ROUTES.LANGUAGES} component={Languages} permissions={LOCALIZATION_LIST} />
            <AuthRoute path={ROUTES.USERS} component={Users} permissions={USER_LIST} />
            <AuthRoute path={ROUTES.AUTHORS} component={Authors} permissions={USER_LIST} devOnly />
            <AuthRoute path={ROUTES.PAYOUTS} component={Payouts} permissions={USER_LIST} devOnly />
            <AuthRoute path={ROUTES.LOCATIONS} component={Locations} permissions={DICTIONARY_LIST} />
            <AuthRoute
              path={ROUTES.BUSINESS_LINES}
              component={(props) => <Dictionaries {...props} {...businessLinesConfig} />}
              permissions={DICTIONARY_LIST}
            />
            <AuthRoute
              path={ROUTES.INTERESTS}
              component={(props) => <Dictionaries {...props} {...interestsConfig} noVerification />}
              permissions={DICTIONARY_LIST}
            />
            <AuthRoute
              path={ROUTES.LANGUAGES_SPOKEN}
              component={(props) => <Dictionaries {...props} {...languagesSpokenConfig} />}
              permissions={DICTIONARY_LIST}
            />
            <AuthRoute path={ROUTES.CONTENT} component={Content} permissions={FAKE_USER_LIST} />
            <AuthRoute
              path={ROUTES.KEYWORDS_VIOLATIONS}
              component={KeyWordsViolations}
              permissions={[KEYWORDS_LIST, KEYWORDS_VIOLATIONS_LIST]}
            />
            <AuthRoute
              path={ROUTES.COMPLAINTS}
              component={Complaints}
              permissions={[COMPLAINTS_LIST]}
            />
            <AuthRoute path={ROUTES.SETTINGS} component={Settings} permissions={SETTINGS_LIST} />
            <AuthRoute path={ROUTES.RADAR} component={Radar} permissions={RADAR_LIST} />
            <AuthRoute path={ROUTES.GIFTS} component={Gifts} permissions={[GIFT_VIEW, GIFT_CATEGORY_VIEW]} />
            <AuthRoute path={ROUTES.PACKAGES} component={Packages} permissions={[MARKET_LIST]} />
            <AuthRoute path={ROUTES.PAID_SERVICES} component={PaidServices} permissions={[MARKET_LIST]} />
            <AuthRoute path={ROUTES.CHATS} component={Chats} />
            <AuthRoute path={ROUTES.JJ_COINS} component={JJCoins} permissions={[COIN_PACKET_VIEW]} />
            <AuthRoute path={ROUTES.JJ_LIKES_PACKAGES} component={JJLikesPackages} permissions={[COIN_PACKET_VIEW]} />
            <AuthRoute path={ROUTES.JJ_FORTUNE} component={JJFortune} permissions={JJFORTUNE_VIEW} />
            <AuthRoute path={ROUTES.JJ_LIKES} component={JJLikes} permissions={[JJFORTUNE_VIEW]} />
            <AuthRoute path={ROUTES.JJ_PREMIUM} component={JJPremium} permissions={[JJFORTUNE_VIEW]} />
            <AuthRoute path={ROUTES.JJ_FUN} component={JJFun} permissions={[JJFORTUNE_VIEW]} />
            <AuthRoute path={ROUTES.JJ_VIDEO} component={JJVideo} permissions={[JJFORTUNE_VIEW]} />
            <AuthRoute path={ROUTES.STATISTICS} component={Statistics} permissions={[JJFORTUNE_VIEW]} />
            <AuthRoute path={ROUTES.AGREEMENTS} component={Agreements} permissions={LOCALIZATION_LIST} />
            <AuthRoute path={ROUTES.ACCRUAL_COINS} component={AccrualCoins} />
            <AuthRoute path={ROUTES.JJ_INCOME} component={JJIncome} permissions={[JJFORTUNE_VIEW]} />
            {/*TODO: add 404 page*/}
            <Route path="*" component={() => <div>404</div>} />
          </Switch>
        </Page>
      </Route>
    </Switch>
  );
};

export default Router;
